import { List } from "immutable";
import moment from "moment";

import CourseExamQuestionsSpec, {
  COURSE_EXAM_QUESTIONS_SPEC_TYPE
} from "seneca-common/features/assignments/state/models/spec/CourseExamQuestionsSpec";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { AssignmentSpecType } from "features/teachers/features/assignment-journey/consts";

import Assignment, {
  ReceivedAssignmentStatus
} from "../../../../models/Assignment";
import CourseSectionsSpec, {
  COURSE_SECTIONS_SPEC_TYPE
} from "../../../../models/spec/CourseSectionsSpec";
import { COURSE_USER_SECTIONS_SPEC_TYPE } from "../../../../models/spec/CourseUserSectionsSpec";
import { getExamQuestionIds, getSectionIds } from "./spec/courseSectionsSpec";
import {
  getAllSectionIds,
  getCourseId as courseUserSectionsCourseId,
  getUserSectionIds as getUserSectionIdsSelector
} from "./spec/courseUserSectionsSpec";

export function getAssignmentId(assignment: Assignment) {
  return assignment.id!;
}

// @ts-ignore
export const getAssignmentCourseId: (arg0: Assignment) => string =
  composeSelector(getSpec as any, courseUserSectionsCourseId);

export function getAssignmentSectionIds(assignment: Assignment): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);
  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getSectionIds(spec as any);
    case COURSE_USER_SECTIONS_SPEC_TYPE:
      return getAllSectionIds(spec as any);
    default:
      return List();
  }
}

export function getAssignmentExamQuestionIds(
  assignment: Assignment
): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getExamQuestionIds(spec as CourseSectionsSpec);
    case COURSE_EXAM_QUESTIONS_SPEC_TYPE:
      return getExamQuestionIds(spec as CourseExamQuestionsSpec);
    default:
      return List();
  }
}

export function assignmentIncludesExamQuestions(assignment: Assignment) {
  return getAssignmentExamQuestionIds(assignment).size > 0;
}

export function assignmentHasNoSections(assignment: Assignment) {
  return getAssignmentSectionIds(assignment).size === 0;
}

export const getUserSectionIds: (
  assignment: Assignment,
  userId: string
) => List<string> = composeSelector(getSpec as any, getUserSectionIdsSelector);

export function getAssignmentNumberOfSections(assignment: Assignment) {
  return getAssignmentSectionIds(assignment).size;
}

export function getAssignmentNumberOfExamQuestions(assignment: Assignment) {
  return getAssignmentExamQuestionIds(assignment).size;
}

export function getClassId(assignment: Assignment) {
  return assignment.classId;
}

export function getStartDate(assignment?: Assignment) {
  return assignment?.startDate;
}

export function getDueDate(assignment: Assignment) {
  return assignment.dueDate;
}

export function getStatus(assignment: Assignment): ReceivedAssignmentStatus {
  return assignment.status!;
}

export function isStatusComplete(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.complete;
}

export function isStatusLate(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.late;
}

export function isStatusIncomplete(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.incomplete;
}

export function getName(assignment: Assignment) {
  return assignment.name;
}

export function getNumAssignees(assignment: Assignment) {
  return assignment.numAssignees;
}

export function getSpecType(assignment: Assignment) {
  return assignment.specType;
}

export function isSmart(assignment: Assignment): boolean {
  return getSpecType(assignment) === COURSE_USER_SECTIONS_SPEC_TYPE;
}

export function isExamQuestionsOnly(assignment: Assignment): boolean {
  return getSpecType(assignment) === AssignmentSpecType.courseExamQuestions;
}

export function getSpec(assignment: Assignment) {
  return assignment.spec;
}

export function getAssignmentCutoffDate(assignment: Assignment) {
  return assignment.assignmentCutoffDate;
}

export function isFutureAssignment(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return (
    now.isBefore(getDueDate(assignment)) &&
    now.isAfter(getStartDate(assignment))
  );
}

export function isPastAssignment(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return now.isAfter(getDueDate(assignment));
}

export function isAssignmentAfterCutoffDate(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return now.isAfter(getAssignmentCutoffDate(assignment));
}
